import type { FC, PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import type { PageDataLoadContextProviderProps } from './PageDataLoadContextProvider.types';
import type { PageDataLoadContextValue } from './context';
import { PageDataLoadContext } from './context';
import { usePreloadPageDataCallback } from './hooks';

const PageDataLoadContextProvider: FC<PropsWithChildren<PageDataLoadContextProviderProps>> = ({
  dataLoader,
  children,
}) => {
  'use memo';

  const [value, setValue] = useState<PageDataLoadContextValue>();
  const preloadPageData = usePreloadPageDataCallback(setValue);
  const { pathname, search } = useLocation();
  const routeParams = useParams();
  const hasPreloadedRef = useRef(false);

  const loadPageData = useCallback(() => {
    if (!dataLoader) {
      return;
    }

    const queryParams = new URLSearchParams(search);
    const resolvedParams = {
      pathname,
      ...routeParams,
      ...Object.fromEntries(queryParams.entries()),
    };

    void preloadPageData(dataLoader, resolvedParams);
    hasPreloadedRef.current = true;
  }, [dataLoader, pathname, preloadPageData, routeParams, search]);

  useEffect(() => {
    if (!hasPreloadedRef.current) {
      loadPageData();
    }
  }, [loadPageData]);

  const retryPageDataLoad = useCallback(() => {
    value?.pageLoadData?.queryRef.dispose();
    setValue(undefined);
    hasPreloadedRef.current = false;
    loadPageData();
  }, [value, loadPageData]);

  const contextValue = useMemo(() => ({ ...value, retryPageDataLoad }), [value, retryPageDataLoad]);

  return <PageDataLoadContext.Provider value={contextValue}>{children}</PageDataLoadContext.Provider>;
};

export default PageDataLoadContextProvider;
